import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import { JOURNEY_STAGES, PROGRESS_PERCENT } from "@/lib/journey";

const Icon = ({ status, size = 12 }) => {
  if (status === "done") return <CheckCircle2 size={size} className="text-[#F47A20]" />;
  if (status === "active") return <Loader2 size={size} className="text-[#F47A20] animate-spin" />;
  return <Clock size={size} className="text-white/35" />;
};

/**
 * JourneyProgress — premium 8-stage indicator shown at the top of every
 * journal page (archive + single entry). `currentSlug` (optional) marks the
 * stage the visitor is currently viewing; completed/active stages with a
 * journal slug deep-link to /journal/:slug.
 */
export const JourneyProgress = ({ currentSlug = null, compact = false }) => {
  return (
    <div
      data-testid="journey-progress"
      data-current-slug={currentSlug || ""}
      className="relative bg-[#0E0E11] border border-white/8 rounded-sm p-4 md:p-5"
    >
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold">
          ROADRIG JOURNEY &middot; YOU ARE HERE
        </div>
        <div className="serial text-[10px] tracking-[0.25em] text-white/45">
          {PROGRESS_PERCENT}% &middot; PHASE 5 / 8
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-[3px] bg-white/8 rounded-full overflow-hidden mb-4">
        <div
          className="absolute inset-y-0 left-0 bg-[#F47A20] transition-[width] duration-700"
          style={{ width: `${PROGRESS_PERCENT}%` }}
        />
      </div>

      {/* Stages */}
      <ol
        className={`grid gap-1.5 ${compact ? "grid-cols-4 sm:grid-cols-8" : "grid-cols-2 sm:grid-cols-4 lg:grid-cols-8"}`}
      >
        {JOURNEY_STAGES.map((s) => {
          const isCurrent = currentSlug && s.slug === currentSlug;
          const clickable = (s.status === "done" || s.status === "active") && s.slug;
          const tone = isCurrent
            ? "border-[#F47A20] bg-[#F47A20]/12 text-white shadow-[0_0_0_1px_rgba(244,122,32,0.45)]"
            : s.status === "active"
            ? "border-[#F47A20]/55 bg-[#F47A20]/[0.06] text-white"
            : s.status === "done"
            ? "border-[#F47A20]/25 bg-[#F47A20]/[0.03] text-white/85 hover:border-[#F47A20]/55"
            : "border-white/8 bg-[#0A0A0C] text-white/45";

          const content = (
            <div className={`p-2 md:p-2.5 border rounded-sm flex flex-col gap-1 transition-colors ${tone} ${clickable ? "cursor-pointer" : ""}`}>
              <div className="flex items-center gap-1.5">
                <Icon status={s.status} />
                <span className="serial text-[9px] tracking-[0.2em] text-white/45 font-bold">
                  {String(s.idx).padStart(2, "0")}
                </span>
                {isCurrent && (
                  <span className="ml-auto serial text-[8px] tracking-[0.2em] text-[#F47A20] font-bold">CURRENT</span>
                )}
              </div>
              <span className={`text-[11px] md:text-[12px] font-bold leading-tight ${isCurrent || s.status === "active" ? "text-white" : ""}`}>
                {s.shortLabel}
              </span>
            </div>
          );

          if (!clickable) {
            return (
              <li
                key={s.key}
                data-testid={`journey-stage-${s.key}`}
                data-status={s.status}
                data-current={isCurrent ? "true" : "false"}
              >
                {content}
              </li>
            );
          }

          return (
            <li
              key={s.key}
              data-testid={`journey-stage-${s.key}`}
              data-status={s.status}
              data-current={isCurrent ? "true" : "false"}
            >
              <Link to={`/journal/${s.slug}`} aria-label={`Open ${s.shortLabel} journal entry`}>
                {content}
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default JourneyProgress;
